import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Radio, ClipboardList, Users, Sparkles, ShieldCheck,
  DollarSign, Wallet, LineChart, Settings, LogOut, Menu, X, ExternalLink,
  Siren, KeyRound, MessageSquareWarning, Inbox,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { AdminUiProvider } from "@/context/AdminUiContext";
import AdminUserProfileDrawer from "@/components/AdminUserProfileDrawer";
import AdminBookingDetailDrawer from "@/components/AdminBookingDetailDrawer";
import { api } from "@/lib/api";
import CleanzaLogo from "@/components/CleanzaLogo";

const NAV = [
  { to: "/admin/dashboard",    label: "Dashboard",           icon: LayoutDashboard },
  { to: "/admin/dispatch",     label: "Live Dispatch",       icon: Radio           },
  { to: "/admin/orders",       label: "Orders / Bookings",   icon: ClipboardList   },
  { to: "/admin/clients",      label: "Clients",             icon: Users           },
  { to: "/admin/cleaners",     label: "Cleaners",            icon: Sparkles        },
  { to: "/admin/verification", label: "Vendor Verification", icon: ShieldCheck     },
  { to: "/admin/messages",     label: "Messages",            icon: Inbox,          badgeKey: "unread" },
  { to: "/admin/risk",         label: "Risk & Safety",       icon: Siren           },
  { to: "/admin/disputes",     label: "Disputes",            icon: MessageSquareWarning },
  { to: "/admin/escrow",       label: "Escrow / Payments",   icon: DollarSign      },
  { to: "/admin/payouts",      label: "Payouts",             icon: Wallet          },
  { to: "/admin/reports",      label: "Reports",             icon: LineChart       },
  { to: "/admin/audit",       label: "Audit Log",           icon: ClipboardList   },
  { to: "/account/security",   label: "Change Password",     icon: KeyRound        },
  { to: "/admin/settings",     label: "Settings",            icon: Settings        },
];

export default function AdminLayout() {
  const { user, logout } = useApp();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/support/unread-count");
        if (!cancelled) setUnread(data?.unread || 0);
      } catch (_) { /* silent */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const handleLogout = () => { logout(); nav("/"); };

  const SidebarInner = (
    <>
      {/* Brand */}
      <div className="px-5 pt-6 pb-5 border-b border-white/5">
        <NavLink
          to="/admin/dashboard"
          onClick={() => setMobileOpen(false)}
          className="flex items-center gap-3 group"
          data-testid="admin-brand"
        >
          <CleanzaLogo variant="icon" height={40} className="drop-shadow-[0_0_18px_rgba(20,184,166,0.35)]" />
          <div>
            <div className="font-display font-black text-lg leading-none flex items-baseline">
              <span className="bg-gradient-to-b from-white to-slate-300 bg-clip-text text-transparent">CLEAN</span>
              <span className="bg-gradient-to-b from-teal-300 to-teal-500 bg-clip-text text-transparent">BAZ</span>
            </div>
            <div className="font-mono text-[9px] uppercase tracking-[0.34em] text-teal-300 mt-1">Admin&nbsp;Console</div>
          </div>
        </NavLink>
      </div>

      {/* Nav items */}
      <nav className="flex-1 overflow-y-auto py-4">
        <ul className="space-y-1 px-3">
          {NAV.map((n) => (
            <li key={n.to}>
              <NavLink
                to={n.to}
                onClick={() => setMobileOpen(false)}
                data-testid={`admin-nav-${n.to.split("/").pop()}`}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-teal-500/15 text-teal-200 border border-teal-400/30 shadow-[inset_0_0_16px_rgba(20,184,166,0.15)]"
                      : "text-slate-300 hover:text-teal-200 hover:bg-white/[0.04] border border-transparent"
                  }`
                }
              >
                <n.icon className="w-4 h-4" />
                <span className="flex-1">{n.label}</span>
                {n.badgeKey === "unread" && unread > 0 && (
                  <span data-testid="admin-nav-unread-badge" className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-red-500/25 text-red-200 border border-red-400/40">{unread}</span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer strip inside sidebar */}
      <div className="border-t border-white/5 px-3 py-3 space-y-1">
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="admin-view-site"
          className="flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-mono uppercase tracking-[0.2em] text-slate-400 hover:text-teal-200 hover:bg-white/[0.04] transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5" /> View public site
        </a>
        <button
          onClick={handleLogout}
          data-testid="admin-logout"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-xs font-mono uppercase tracking-[0.2em] text-red-300/80 hover:text-red-200 hover:bg-red-500/10 transition-colors"
        >
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex" data-testid="admin-shell">
      {/* Sidebar — desktop */}
      <aside className="hidden lg:flex w-64 flex-col bg-slate-950/95 border-r border-white/5 sticky top-0 h-screen">
        {SidebarInner}
      </aside>

      {/* Sidebar — mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="lg:hidden fixed z-50 top-0 left-0 h-screen w-64 flex flex-col bg-slate-950 border-r border-white/5 animate-in slide-in-from-left duration-200">
            {SidebarInner}
          </aside>
        </>
      )}

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header
          className="sticky top-0 z-30 bg-slate-950/85 backdrop-blur-xl border-b border-white/5"
          data-testid="admin-topbar"
        >
          <div className="px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between gap-4">
            <button
              className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-lg border border-white/10 text-slate-100 hover:text-teal-300"
              onClick={() => setMobileOpen(true)}
              aria-label="Open admin navigation"
              data-testid="admin-mobile-toggle"
            >
              <Menu className="w-4 h-4" />
            </button>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400 truncate">
              Platform Admin · Command Center
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-teal-500/10 border border-teal-400/30 text-teal-200 text-[11px] font-mono uppercase tracking-[0.2em]">
                <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />
                {user?.role || "admin"}
              </span>
              <div className="hidden sm:block text-xs text-slate-300 max-w-[140px] truncate">{user?.full_name || user?.email}</div>
              <button
                onClick={handleLogout}
                className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-lg border border-red-400/40 text-red-300 hover:text-red-200 hover:bg-red-500/10"
                aria-label="Sign out"
                data-testid="admin-mobile-logout"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </header>

        {/* Routed section */}
        <main className="flex-1 min-w-0">
          <AdminUiProvider>
            <Outlet />
            <AdminUserProfileDrawer />
            <AdminBookingDetailDrawer />
          </AdminUiProvider>
        </main>
      </div>
    </div>
  );
}
